import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { AuthenticationService } from '@app/core/authentication/authentication.service';
import { Features, UserPermissions } from '@app/core/user-permissions/user-permissions.model';
import { UsersFacade } from '@app/shared/+state/users';
import { User } from '@app/shared/interfaces';

@Component({
  standalone: false,
  selector: 'sbnb-user-management-features-selector',
  templateUrl: './user-management-features-selector.component.html',
  styleUrls: ['./user-management-features-selector.component.scss'],
})
export class UserManagementFeaturesSelectorComponent implements OnInit {
  features: { id: string; icon: string; label: string; children?: any[] }[];
  allFeatures: boolean;

  user: User = this.auth.getUserDetails();

  @Input() userPermissions: UserPermissions;
  @Output() permissionsChanged: EventEmitter<any> = new EventEmitter<any>();

  constructor(
    private auth: AuthenticationService,
    private readonly usersFacade: UsersFacade
  ) { }

  ngOnInit() {
    if (!this.userPermissions) {
      console.error('User permissions are missing');
      return;
    }
    this.ensureReservationsPermIsEnabled(); // reservations should always be checked
    this.buildFeatures();
    this.checkIndeterminateState();
  }

  buildFeatures() {
    this.features = [
      {
        id: 'reservations',
        icon: 'inbox',
        label: 'Reservations',
        children: [
          {
            id: Features.PERM_RESERVATION_FINANCIALS,
            label: 'Display financial data',
          },
          {
            id: Features.PERM_MANAGE_RESERVATIONS,
            label: 'Manage Reservations',
          }
        ],
      },
      {
        id: Features.PERM_INBOX,
        icon: 'inbox',
        label: 'Inbox',
        children: [
          {
            id: Features.PERM_PURCHASE_ADDITIONAL_SERVICES,
            label: 'Purchase additional services',
            tooltip: 'Users with this permission are able to purchase additional services from within the Inbox, like Guest Verification.',
          },
        ],
      },
      {
        id: Features.PERM_CALENDAR,
        icon: 'calendar',
        label: 'Calendar',
        children: [
          {
            id: Features.PERM_CALENDAR_DISPLAY_PRICING,
            label: 'Display pricing',
          },
          {
            id: Features.PERM_CALENDAR_UPDATE_PRICING_AND_AVAIL,
            label: 'Update pricing',
          },
          {
            id: Features.PERM_CALENDAR_UPDATE_AVAILABILITY,
            label: 'Update availability and min stay',
          },
        ],
      },
      {
        id: Features.PERM_PROPERTIES,
        icon: 'properties',
        label: 'Properties',
      },
      {
        id: Features.PERM_GUEST_EXPERIENCE,
        icon: 'gx',
        label: `Guest Experience  <span class="badge">All properties</span>`,
      },
      {
        id: Features.PERM_OPERATIONS,
        icon: 'ops',
        label: `Operations <span class="badge">All properties</span>`,
      },
      {
        id: Features.PERM_TASKS,
        icon: 'tasks',
        label: 'Tasks',
        children: [
          {
            id: Features.PERM_TASKS_UPDATE_YOURS,
            label: 'Update their tasks',
          },
          {
            id: Features.PERM_TASKS_UPDATE_ALL,
            label: 'Update all tasks',
          },
        ],
      },
      {
        id: Features.PERM_APPS,
        icon: 'apps',
        label: 'Apps',
      },
      {
        id: Features.PERM_METRICS,
        icon: 'metrics',
        label: 'Metrics',
      },
      {
        id: Features.PERM_DIRECT,
        icon: 'direct',
        label: 'Direct',
        children: [
          {
            id: Features.PERM_DIRECT_DISPLAY_FINANCIAL_DATA,
            label: 'Display financial data',
          },
        ],
      },
      {
        id: Features.PERM_SMARTLOCKS_DASHBOARD,
        icon: 'nav-devices',
        label: 'Devices',
        children: [
          {
            id: Features.PERM_SMARTLOCKS_DASHBOARD_WRITE,
            label: 'Create, Update, Delete, Unlock/Lock',
          },
        ],
      },
    ];

    // Add owner statements feature if user has access to it
    this.usersFacade.ownerStatementsEntitlement$.subscribe((ownerStatementsEntitlement) => {
      if (!ownerStatementsEntitlement?.available) {
        return;
      }
      const opsFeature = this.features.find((feature) => feature.id === Features.PERM_OPERATIONS);
      if (!opsFeature) {
        return;
      }
      opsFeature['children'] = [
        {
          id: Features.PERM_OPERATIONS_OWNER_STATEMENTS,
          label: 'Owner statements',
        },
      ];
    });
  }

  selectAll() {
    Object.keys(this.userPermissions).forEach((permKey) => {
      this.userPermissions[permKey] = true;
      this.ensureReservationsPermIsEnabled();
    });
  }

  allFeaturesChanged(checked: boolean) {
    this.features.forEach((key) => {
      if (key.children) {
        for (let i = 0; i < key.children.length; i++) {
          this.userPermissions[key.children[i].id] = checked;
        }
      }
      this.userPermissions[key.id] = checked;
    });
    this.ensureReservationsPermIsEnabled();
    this.checkIndeterminateState();
  }

  clearFeatures() {
    Object.keys(this.userPermissions).forEach((key) => {
      this.userPermissions[key] = false;
      this.checkIndeterminateState();
    });
  }

  checkIndeterminateState() {
    const checkedItems = Object.keys(this.userPermissions).filter((permKey) => {
      if (permKey === 'all_properties') {
        // exclude all_properties key as it shows up sometimes and isn't relevant this this section
        return false;
      }
      return this.userPermissions[permKey] === true;
    });
    this.ensureReservationsPermIsEnabled();
    this.permissionsChanged.emit(this.userPermissions);
    if (checkedItems.length === this.userPermissions.features.length) {
      this.allFeatures = true;
    } else {
      this.allFeatures = false;
    }
  }

  disableChildrenIfParentIsUnchecked(parentId: string, parentChecked: boolean) {
    // If parent has just been checked, we take no action on the children
    if (parentChecked === true) {
      return;
    }

    // find the parent in the features structure
    const parentObj = this.features.find((x) => x.id === parentId);

    // If the parent isn't found, or it has no children, nothing to do here
    if (!parentObj || (parentObj && (!parentObj.children || parentObj.children.length === 0))) {
      return;
    }

    parentObj.children.forEach((child) => {
      if (child.id) {
        this.userPermissions[child.id] = false;
      }
    });
  }

  ensureReservationsPermIsEnabled() {
    this.userPermissions[Features.PERM_RESERVATIONS] = true; // reservations should always be checked
  }
}
